import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Heading, Text } from "@chakra-ui/react";
import { PlanType } from "sdk/types";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { useTrialDiscount } from "~/MemberApp/hooks";
import { Trans } from "~/utils/i18next";
import { MembershipSelectAction } from "../hooks";
import { PlanBoxAmount } from "./PlanBoxAmount";
import { PlanCta } from "./PlanCta";

interface Props {
  action: MembershipSelectAction;
  flourish?: ReactNode;
  handleClick: () => void;
  type: PlanType;
}

export const PlanBox: FC<Props> = ({ action, flourish, handleClick, type }) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const trialDiscount = useTrialDiscount();
  if (!user) return null;
  const base = `planSelect.${action}.${type}` as const;
  const showRecommendedBadge = type == "premium" && !trialDiscount;
  const planTypeProps =
    type == "plus"
      ? {
          buttonVariant: "th-secondary" as const,
        }
      : {
          buttonVariant: "th-primary" as const,
        };

  return (
    <Flex id={type} maxW="31.25rem" position="relative" w="full">
      {flourish}
      <Flex
        align="center"
        bgColor="th.white"
        borderRadius="2xl"
        boxShadow="xl"
        direction="column"
        justify="space-between"
        id={type}
        gap={{ base: 3, md: 6 }}
        position="relative"
        px={{ base: 4, md: 14 }}
        py={{ base: 8, md: 14 }}
        w="full"
      >
        {showRecommendedBadge && (
          <Flex
            bg="th.blueGreen1"
            justify="center"
            position="absolute"
            px={4}
            py={2}
            rounded="full"
            top={-4}
          >
            <Text variant="th-t3">{t("planSelect.recommended")}</Text>
          </Flex>
        )}
        <Flex direction="column" mb={{ base: 3, md: 0 }}>
          <Heading as="h2" mb={2} textAlign="center" variant="th-h3">
            {t(`${base}.heading`)}
          </Heading>

          <Text
            textAlign="center"
            variant={{ base: "th-body3", md: "th-body2" }}
          >
            <Trans i18nKey={`${base}.intro`} />
          </Text>
        </Flex>

        <Flex direction="column" gap={6} w="full">
          <Flex align="center" direction="column">
            <Text
              color="th.blueGreen2"
              fontWeight="bold"
              textTransform="uppercase"
              variant={{ base: "th-body3" }}
            >
              <Trans i18nKey={`${base}.price.startingAt`} />
            </Text>
            <PlanBoxAmount action={action} plan={type} type={type} />
            <Text
              color="th.gray4"
              mt={2}
              variant={{ base: "th-body3", md: "th-body1" }}
            >
              <Trans i18nKey={`${base}.price.freeTrial`} />
            </Text>
          </Flex>

          {action == "upgrade" && type == "plus" ? (
            <Flex align="center" h="3.375rem" justify="center" w="full">
              <Text variant="th-t3">{t("planSelect.currentPlanStatic")}</Text>
            </Flex>
          ) : (
            <PlanCta
              action={action}
              handleClick={handleClick}
              type={type}
              variant={planTypeProps.buttonVariant}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
